/**
 * V3 — "Immersive"
 * Full-bleed photography IS the design. Glassmorphism panels float over
 * cinematic DJ/crowd shots. mix-blend-mode text. Parallax depth. Every
 * section is a visual experience, not a card grid.
 * Fonts: Syne (display) + Manrope (body)
 */
import { useLayoutEffect } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ArrowRight, FolderOpen, Sparkles, Users, Zap } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

const D = "'Syne', sans-serif";
const B = "'Manrope', sans-serif";
const TEAL = '#2EC4B6';

const Glass = ({ children, className = '', style = {} }: { children: React.ReactNode; className?: string; style?: React.CSSProperties }) => (
  <div className={`backdrop-blur-xl ${className}`} style={{
    background: 'rgba(10,14,15,0.6)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 16,
    ...style,
  }}>{children}</div>
);

export function LandingV3() {
  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      // Hero text reveal
      gsap.fromTo('.v3i-hero-text', { y: 40, opacity: 0 }, { y: 0, opacity: 1, stagger: 0.15, duration: 0.8, ease: 'power3.out', delay: 0.2 });

      // Parallax backgrounds
      document.querySelectorAll('.v3i-parallax').forEach((el) => {
        gsap.to(el, {
          y: '-15%', ease: 'none',
          scrollTrigger: { trigger: el.parentElement!, start: 'top bottom', end: 'bottom top', scrub: true },
        });
      });

      // Glass panels slide up
      document.querySelectorAll('.v3i-glass').forEach((el) => {
        gsap.fromTo(el, { y: 60, opacity: 0 }, {
          y: 0, opacity: 1, duration: 0.7, ease: 'power2.out',
          scrollTrigger: { trigger: el, start: 'top 85%' },
        });
      });

      // Blend text reveal
      gsap.fromTo('.v3i-blend', { scale: 1.1, opacity: 0 }, {
        scale: 1, opacity: 1, duration: 1,
        scrollTrigger: { trigger: '.v3i-blend', start: 'top 70%' },
      });
    });
    return () => ctx.revert();
  }, []);

  return (
    <div style={{ fontFamily: B, background: '#060809', color: '#c8cdd0', minHeight: '100vh', overflowX: 'hidden' }}>
      {/* Nav — glass */}
      <nav className="fixed top-0 left-0 right-0 z-50">
        <div className="max-w-[1400px] mx-auto px-6 pt-4">
          <Glass className="flex items-center justify-between px-6 h-14" style={{ borderRadius: 12 }}>
            <Link to="/" className="flex items-center gap-3">
              <img src="/eventtools-logo.svg" alt="" className="w-7 h-7" />
              <span style={{ fontFamily: D, fontWeight: 700, fontSize: 15, color: '#fff' }}>Event.Tools</span>
            </Link>
            <Link to="/signin" className="px-5 py-2 text-xs font-bold tracking-wider uppercase rounded-lg" style={{ background: TEAL, color: '#060809' }}>
              Get access
            </Link>
          </Glass>
        </div>
      </nav>

      {/* Hero: full-screen photo, mix-blend text */}
      <section className="relative h-screen overflow-hidden">
        <div className="v3i-parallax absolute inset-0 scale-[1.15]">
          <img src="/images/hero_crowd.jpg" alt="" className="w-full h-full object-cover" style={{ filter: 'brightness(0.45) saturate(1.3)' }} />
        </div>
        <div className="absolute inset-0" style={{ background: `radial-gradient(ellipse at 50% 80%, ${TEAL}10, transparent 60%)` }} />
        <div className="relative z-10 h-full flex flex-col justify-center items-center text-center px-6">
          <h1 className="v3i-hero-text" style={{
            fontFamily: D, fontSize: 'clamp(52px, 10vw, 140px)', fontWeight: 800,
            lineHeight: 0.92, color: '#fff', letterSpacing: '-0.05em',
            mixBlendMode: 'difference',
          }}>
            YOUR<br />ASSETS
          </h1>
          <p className="v3i-hero-text mt-4" style={{ fontFamily: D, fontSize: 'clamp(20px, 3vw, 36px)', fontWeight: 300, color: TEAL, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
            Always Ready
          </p>
          <p className="v3i-hero-text mt-6 max-w-md text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.5)' }}>
            The flyer builder that organizes every brand element so AI generation always uses the right context.
          </p>
          <div className="v3i-hero-text mt-8 flex gap-4">
            <Link to="/signin" className="px-6 py-3 text-sm font-bold rounded-lg" style={{ background: TEAL, color: '#060809' }}>
              Get early access
            </Link>
          </div>
          {/* Scroll indicator */}
          <div className="v3i-hero-text absolute bottom-10 left-1/2 -translate-x-1/2">
            <div className="w-6 h-10 rounded-full border border-white/20 flex items-start justify-center pt-2">
              <div className="w-1 h-2 rounded-full bg-white/40 animate-bounce" />
            </div>
          </div>
        </div>
      </section>

      {/* Section: Photo bg + glass panel — Asset Library */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        <div className="v3i-parallax absolute inset-0 scale-[1.15]">
          <img src="/images/collab_dj_booth.jpg" alt="" className="w-full h-full object-cover" style={{ filter: 'brightness(0.3) saturate(1.2)' }} />
        </div>
        <div className="relative z-10 w-full px-6 py-24">
          <div className="max-w-[1200px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div className="v3i-glass">
              <Glass className="p-8 lg:p-10">
                <div className="flex items-center gap-3 mb-6">
                  <FolderOpen size={20} style={{ color: TEAL }} />
                  <span className="text-xs font-bold tracking-[0.2em] uppercase" style={{ color: TEAL }}>Asset Library</span>
                </div>
                <h2 style={{ fontFamily: D, fontSize: 'clamp(28px, 3.5vw, 44px)', fontWeight: 800, color: '#fff', lineHeight: 1.05, letterSpacing: '-0.03em' }}>
                  Everything in one place.
                </h2>
                <p className="mt-4 text-sm leading-relaxed" style={{ color: '#889' }}>
                  Logos, headshots, backgrounds, brand elements — organized by artist, venue, and event. Never dig through folders again.
                </p>
              </Glass>
            </div>
            <div className="v3i-glass">
              <Glass className="p-2 overflow-hidden">
                <img src="/screenshot-library.png" alt="" className="w-full rounded-xl" />
              </Glass>
            </div>
          </div>
        </div>
      </section>

      {/* Section: Photo bg + glass panel — Entity Profiles */}
      <section className="relative min-h-[80vh] flex items-center overflow-hidden">
        <div className="v3i-parallax absolute inset-0 scale-[1.15]">
          <img src="/images/planning_dj_headphones.jpg" alt="" className="w-full h-full object-cover" style={{ filter: 'brightness(0.25) saturate(1.3)' }} />
        </div>
        <div className="relative z-10 w-full px-6 py-20">
          <div className="max-w-[600px] mx-auto v3i-glass">
            <Glass className="p-8 lg:p-10 text-center">
              <Users size={24} style={{ color: TEAL }} className="mx-auto mb-4" />
              <h2 style={{ fontFamily: D, fontSize: 'clamp(28px, 3.5vw, 40px)', fontWeight: 800, color: '#fff', lineHeight: 1.05, letterSpacing: '-0.03em' }}>
                Profiles for every artist & venue.
              </h2>
              <p className="mt-4 text-sm leading-relaxed" style={{ color: '#889' }}>
                Link assets to the people and places they belong to. When you generate a flyer, AI knows exactly which logos, headshots, and brand colors to use.
              </p>
            </Glass>
          </div>
        </div>
      </section>

      {/* Full-bleed photo with blend-mode text */}
      <section className="relative h-[70vh] overflow-hidden flex items-center justify-center">
        <div className="v3i-parallax absolute inset-0 scale-[1.15]">
          <img src="/images/distribute_dj_lasers.jpg" alt="" className="w-full h-full object-cover" style={{ filter: 'brightness(0.5) saturate(1.4)' }} />
        </div>
        <h2 className="v3i-blend relative z-10 text-center px-6" style={{
          fontFamily: D, fontSize: 'clamp(40px, 8vw, 100px)', fontWeight: 800,
          color: '#fff', letterSpacing: '-0.05em', lineHeight: 0.95,
          mixBlendMode: 'difference',
        }}>
          Grounded in<br />your brand.
        </h2>
      </section>

      {/* Section: AI Generation + Product shot */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        <div className="v3i-parallax absolute inset-0 scale-[1.15]">
          <img src="/images/create_dj_backlit.jpg" alt="" className="w-full h-full object-cover" style={{ filter: 'brightness(0.25) saturate(1.2)' }} />
        </div>
        <div className="relative z-10 w-full px-6 py-24">
          <div className="max-w-[1200px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div className="v3i-glass order-2 lg:order-1">
              <Glass className="p-2 overflow-hidden">
                <img src="/screenshot-events.png" alt="" className="w-full rounded-xl" />
              </Glass>
            </div>
            <div className="v3i-glass order-1 lg:order-2">
              <Glass className="p-8 lg:p-10">
                <div className="flex items-center gap-3 mb-6">
                  <Sparkles size={20} style={{ color: TEAL }} />
                  <span className="text-xs font-bold tracking-[0.2em] uppercase" style={{ color: TEAL }}>AI Generation</span>
                </div>
                <h2 style={{ fontFamily: D, fontSize: 'clamp(28px, 3.5vw, 44px)', fontWeight: 800, color: '#fff', lineHeight: 1.05, letterSpacing: '-0.03em' }}>
                  Context-aware flyers.
                </h2>
                <p className="mt-4 text-sm leading-relaxed" style={{ color: '#889' }}>
                  Unlike generic AI, Event.Tools generates with your real logos, headshots, and brand identity. Select an event, hit generate, done.
                </p>
                <div className="mt-6 flex flex-wrap gap-2">
                  {['Artist logos', 'Venue colors', 'Event lineup'].map((t) => (
                    <span key={t} className="px-3 py-1.5 rounded-full text-xs font-medium" style={{ background: `${TEAL}15`, color: TEAL }}>{t}</span>
                  ))}
                </div>
              </Glass>
            </div>
          </div>
        </div>
      </section>

      {/* Workflow — floating glass cards */}
      <section className="relative min-h-[70vh] flex items-center overflow-hidden">
        <div className="v3i-parallax absolute inset-0 scale-[1.15]">
          <img src="/images/tools_crowd_hands.jpg" alt="" className="w-full h-full object-cover" style={{ filter: 'brightness(0.2) saturate(1.2)' }} />
        </div>
        <div className="relative z-10 w-full px-6 py-20">
          <div className="max-w-[1100px] mx-auto">
            <h2 className="v3i-glass text-center mb-12" style={{ fontFamily: D, fontSize: 'clamp(32px, 4vw, 52px)', fontWeight: 800, color: '#fff', letterSpacing: '-0.04em' }}>
              Upload once. Generate forever.
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {[
                { icon: FolderOpen, n: '01', t: 'Upload', d: 'Add your logos, headshots, brand assets.' },
                { icon: Users, n: '02', t: 'Organize', d: 'Link assets to artists and venues.' },
                { icon: Zap, n: '03', t: 'Generate', d: 'One click. Print-ready flyer.' },
              ].map((s, i) => (
                <div key={i} className="v3i-glass">
                  <Glass className="p-6 text-center">
                    <span style={{ fontFamily: D, fontSize: 32, fontWeight: 800, color: `${TEAL}25` }}>{s.n}</span>
                    <s.icon size={24} style={{ color: TEAL }} className="mx-auto mt-2 mb-3" />
                    <h3 className="font-bold text-white mb-2" style={{ fontFamily: D }}>{s.t}</h3>
                    <p className="text-xs" style={{ color: '#778' }}>{s.d}</p>
                  </Glass>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
        <div className="v3i-parallax absolute inset-0 scale-[1.15]">
          <img src="/images/teams_dj_smoke.jpg" alt="" className="w-full h-full object-cover" style={{ filter: 'brightness(0.25) saturate(1.3)' }} />
        </div>
        <div className="absolute inset-0" style={{ background: `radial-gradient(ellipse at center, ${TEAL}08, transparent 70%)` }} />
        <div className="relative z-10 text-center px-6 v3i-glass">
          <Glass className="px-12 py-14">
            <h2 style={{ fontFamily: D, fontSize: 'clamp(32px, 4vw, 52px)', fontWeight: 800, color: '#fff', letterSpacing: '-0.04em' }}>
              Ready?
            </h2>
            <p className="mt-3 mb-8 text-sm" style={{ color: '#778' }}>Join early access. Never hunt for assets again.</p>
            <Link to="/signin" className="inline-flex items-center gap-2 px-7 py-3.5 text-sm font-bold rounded-lg" style={{ background: TEAL, color: '#060809' }}>
              Get early access <ArrowRight size={16} />
            </Link>
          </Glass>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6" style={{ background: '#060809' }}>
        <div className="max-w-[1400px] mx-auto flex items-center justify-between">
          <span style={{ fontFamily: D, fontWeight: 700, fontSize: 13, color: '#444' }}>Event.Tools</span>
          <span className="text-[11px]" style={{ color: '#333' }}>© {new Date().getFullYear()}</span>
        </div>
      </footer>
    </div>
  );
}
